/**
 * Transport for the chat widget → `POST /chat` on the marketbrewer Lambda API.
 *
 * Same base URL and deadline discipline as ContactForm.tsx. Response handling follows the
 * contract the N&S forms learned the hard way: a 2xx whose body says `success: false` is a
 * failure, and a hung connection must end in an error the visitor can retry.
 */

const FALLBACK_API_URL = "https://wcvi65m780.execute-api.us-east-1.amazonaws.com/prod";
const apiBase = (): string => process.env.API_BASE_URL || FALLBACK_API_URL;

const CHAT_TIMEOUT_MS = 25_000;
const FORM_TIMEOUT_MS = 15_000;

export type Failure = "timeout" | "network" | "http" | "rejected";

type Json = Record<string, unknown>;

interface Transport {
  ok: boolean;
  status: number;
  body: Json | null;
  failure?: Failure;
}

async function post(path: string, payload: Json, timeoutMs: number): Promise<Transport> {
  try {
    const response = await fetch(`${apiBase()}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(timeoutMs),
    });
    const body = (await response.json().catch(() => null)) as Json | null;
    if (!response.ok) return { ok: false, status: response.status, body, failure: "http" };
    if (body && body.success === false) return { ok: false, status: response.status, body, failure: "rejected" };
    return { ok: true, status: response.status, body };
  } catch (error) {
    if (error instanceof DOMException && error.name === "TimeoutError") {
      return { ok: false, status: 0, body: null, failure: "timeout" };
    }
    return { ok: false, status: 0, body: null, failure: "network" };
  }
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const num = (v: unknown, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

export type StartResult =
  | { ok: true; conversationId: string; mode: "ai" | "form"; turnsLeft: number }
  | { ok: false; failure: Failure; status: number };

export async function startChat(input: {
  language: "en" | "es";
  page: string;
  renderTime: number;
  hp: string;
}): Promise<StartResult> {
  const r = await post(
    "/chat",
    { action: "start", language: input.language, page: input.page, renderTime: String(input.renderTime), hp: input.hp },
    FORM_TIMEOUT_MS,
  );
  const id = str(r.body?.conversationId);
  if (!r.ok || !id) return { ok: false, failure: r.failure ?? "http", status: r.status };
  return {
    ok: true,
    conversationId: id,
    mode: r.body?.mode === "form" ? "form" : "ai",
    turnsLeft: num(r.body?.turnsLeft, 8),
  };
}

export type MessageResult =
  | { ok: true; mode: "ai"; reply: string; turnsLeft: number }
  | { ok: true; mode: "form"; reason: string; turnsLeft: number }
  | { ok: false; failure: Failure; status: number; error: string | null };

export async function sendChatMessage(input: { conversationId: string; text: string }): Promise<MessageResult> {
  const r = await post("/chat", { action: "message", conversationId: input.conversationId, text: input.text }, CHAT_TIMEOUT_MS);
  if (!r.ok) {
    return { ok: false, failure: r.failure ?? "http", status: r.status, error: str(r.body?.error) || null };
  }
  const turnsLeft = num(r.body?.turnsLeft, 0);
  if (r.body?.mode === "ai" && str(r.body?.reply)) {
    return { ok: true, mode: "ai", reply: str(r.body?.reply), turnsLeft };
  }
  return { ok: true, mode: "form", reason: str(r.body?.reason) || "form", turnsLeft };
}

export interface LeadInput {
  conversationId: string | null;
  language: "en" | "es";
  name: string;
  email: string;
  phone: string;
  business: string;
  message: string;
  hp: string;
  /** What the visitor and assistant said, for the fallback path only. */
  transcript: string;
}

export type LeadResult = { ok: true; via: "chat" | "contact-form" } | { ok: false; failure: Failure; status: number; error: string | null };

/**
 * Saves the visitor's details. With a live conversation this goes to `/chat`; without one
 * (the chat Lambda unreachable or not yet deployed) it falls back to the contact-form
 * endpoint every other form on the site uses, so the lead is never lost.
 */
export async function submitChatLead(input: LeadInput): Promise<LeadResult> {
  if (input.conversationId) {
    const r = await post(
      "/chat",
      {
        action: "lead",
        conversationId: input.conversationId,
        name: input.name,
        email: input.email,
        phone: input.phone,
        business: input.business,
        message: input.message,
        hp: input.hp,
      },
      FORM_TIMEOUT_MS,
    );
    if (r.ok) return { ok: true, via: "chat" };
    // A vanished conversation (404) is the one case worth retrying through the other door.
    if (r.status !== 404) return { ok: false, failure: r.failure ?? "http", status: r.status, error: str(r.body?.error) || null };
  }

  const noteLines = [
    "[Sent from the website chat widget — assistant unavailable]",
    input.message.trim() ? input.message.trim() : "",
    input.transcript.trim() ? `\nConversation so far:\n${input.transcript.trim()}` : "",
  ].filter(Boolean);
  const message = noteLines.join("\n");
  const r = await post(
    "/form-submission",
    {
      name: input.name,
      email: input.email,
      phone: input.phone,
      // The contact Lambda requires at least 10 characters; the tag line alone satisfies that.
      message: message.length >= 10 ? message : `${message} — please follow up.`,
      subject: `Chat widget - ${input.language === "es" ? "Español" : "English"}`,
      website: input.hp,
      businessName: input.business,
      websiteUrl: "",
      dmvLocal: "",
      language: input.language,
      renderTime: "",
    },
    FORM_TIMEOUT_MS,
  );
  if (r.ok) return { ok: true, via: "contact-form" };
  return { ok: false, failure: r.failure ?? "http", status: r.status, error: str(r.body?.error) || null };
}

/** Fire-and-forget: lets the Lambda write its summary when a real conversation ends. */
export function endChat(conversationId: string): void {
  const payload = JSON.stringify({ action: "end", conversationId });
  const url = `${apiBase()}/chat`;
  // sendBeacon survives tab close and cannot set Content-Type: application/json on all
  // browsers; the Lambda parses the body regardless of the header, and the beacon carries
  // no CORS preflight for text/plain, which is exactly what makes it reliable on unload.
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      if (navigator.sendBeacon(url, new Blob([payload], { type: "text/plain" }))) return;
    } catch {
      // fall through to fetch
    }
  }
  void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {
    /* best effort */
  });
}
